import React, { createContext, PropsWithChildren, useContext } from 'react';

import { useGetStudy } from '../hooks/study/useGetStudy';
import { useGetStudyIntake } from '../hooks/study/useGetStudyIntake';
import { Study } from '../models/Study';
import { StudyParticipant } from '../models/StudyParticipant';

export type StudyContextProps = ReturnType<typeof useGetStudyIntake> &
  ReturnType<typeof useGetStudy> & {
    studyParticipantId?: number;
    study?: Study;
    studyParticipant?: StudyParticipant;
  };

export const StudyContext = createContext<StudyContextProps>(null!);

export const useStudyContext = () => {
  const context = useContext(StudyContext);
  if (!context) {
    throw new Error(
      'useStudyContext must be used within the context of the StudyContextProvider component'
    );
  }
  return context;
};

export interface StudyContextProviderProps {
  /**
   * Pass in the studyParticipantId to load a participant's study data.
   */
  studyParticipantId?: number;
  /**
   * Pass in the studyId to get only study data
   */
  studyId?: number;
}

/**
 * The StudyContextProvider can be used to load the Study Participant data OR
 * just the Study data. Components using the StudyContext should be
 * able to handle empty states where Study Participant data is not available.
 */
export const StudyContextProvider: React.FC<
  PropsWithChildren & StudyContextProviderProps
> = ({ studyParticipantId, studyId, children }) => {
  const getStudyIntakeApi = useGetStudyIntake();
  const getStudyApi = useGetStudy(studyId || 0);
  const { studyIntake } = getStudyIntakeApi;
  const { study } = getStudyApi;
  return (
    <StudyContext.Provider
      value={{
        studyParticipantId,
        ...getStudyIntakeApi,
        ...getStudyApi,
        study: study,
        studyParticipant: studyIntake?.studyParticipant
      }}
    >
      {children}
    </StudyContext.Provider>
  );
};
